/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ParkingLotGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { Globe } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import type { ParkingLotFormState } from '../types';

interface ParkingLotGeneralSectionProps {
    formData: ParkingLotFormState;
    errors: Record<string, string>;
    onChange: (field: keyof ParkingLotFormState, value: ParkingLotFormState[keyof ParkingLotFormState]) => void;
    t: (key: string) => string;
}

export function ParkingLotGeneralSection({ formData, errors, onChange, t }: ParkingLotGeneralSectionProps) {
    return (
        <FormBox title={t('parkingLots.sections.generalInfo')} icon={<Globe className="h-5 w-5" />}>
            {/* Row 1: extension + name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                <Label htmlFor="extension">
                    <SimpleTooltip content={t('parkingLots.fields.extension.tooltip')}>
                        <span>{t('parkingLots.fields.extension.label')}<span className="text-red-500 ml-1">*</span></span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="extension"
                        value={formData.extension}
                        onChange={(e) => onChange('extension', e.target.value)}
                        placeholder={t('parkingLots.fields.extension.placeholder')}
                        className={errors.extension ? 'border-red-500' : ''}
                    />
                    {errors.extension && <p className="text-sm text-red-500 mt-1">{errors.extension}</p>}
                </div>

                <Label htmlFor="name">
                    <SimpleTooltip content={t('parkingLots.fields.name.unique')}>
                        <span>{t('parkingLots.fields.name.label')}<span className="text-red-500 ml-1">*</span></span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('parkingLots.fields.name.placeholder')}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                </div>
            </div>

            {/* Row 2: totalPositions + findSlot + parkingTime + comebackTime */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center mt-6">
                <Label htmlFor="totalPositions">
                    <SimpleTooltip content={t('parkingLots.fields.totalPositions.tooltip')}>
                        <span>{t('parkingLots.fields.totalPositions.label')}<span className="text-red-500 ml-1">*</span></span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="totalPositions"
                        type="number"
                        min={1}
                        max={99}
                        value={formData.totalPositions}
                        onChange={(e) => onChange('totalPositions', parseInt(e.target.value, 10) || 10)}
                        className={errors.totalPositions ? 'border-red-500' : ''}
                    />
                    {errors.totalPositions && <p className="text-sm text-red-500 mt-1">{errors.totalPositions}</p>}
                </div>

                <Label htmlFor="findSlot">
                    <SimpleTooltip content={t('parkingLots.fields.findSlot.tooltip')}>
                        {t('parkingLots.fields.findSlot.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Select value={formData.findSlot} onValueChange={(value) => onChange('findSlot', value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="next">{t('parkingLots.fields.findSlot.options.next')}</SelectItem>
                            <SelectItem value="random">{t('parkingLots.fields.findSlot.options.random')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <Label htmlFor="parkingTime">
                    <SimpleTooltip content={t('parkingLots.fields.parkingTime.tooltip')}>
                        {t('parkingLots.fields.parkingTime.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="parkingTime"
                        type="number"
                        min={5}
                        max={300}
                        value={formData.parkingTime}
                        onChange={(e) => onChange('parkingTime', parseInt(e.target.value, 10) || 45)}
                        className={errors.parkingTime ? 'border-red-500' : ''}
                    />
                    {errors.parkingTime && <p className="text-sm text-red-500 mt-1">{errors.parkingTime}</p>}
                </div>

                <Label htmlFor="comebackTime">
                    <SimpleTooltip content={t('parkingLots.fields.comebackTime.tooltip')}>
                        {t('parkingLots.fields.comebackTime.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="comebackTime"
                        type="number"
                        min={5}
                        max={300}
                        value={formData.comebackTime}
                        onChange={(e) => onChange('comebackTime', parseInt(e.target.value, 10) || 30)}
                        className={errors.comebackTime ? 'border-red-500' : ''}
                    />
                    {errors.comebackTime && <p className="text-sm text-red-500 mt-1">{errors.comebackTime}</p>}
                </div>
            </div>
        </FormBox>
    );
}
